if (!customElements.get("video-background")) {
  customElements.define(
    "video-background",
    class VideoBackground extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.intersecting = false;
        this.videoLoaded = false;
        this.ytPlayer = null;
        this.vimeoPlayer = null;
        this.isPlaying = false;
      }

      connectedCallback() {
        this.videoType = this.dataset.type;
        this.videoId = this.dataset.videoId;
        this.sectionId = this.dataset.sectionId;
        this.clickToPlay = this.dataset.clickToPlay === "true";
        this.videoElement = this.querySelector(`#${this.sectionId}-video-player`);
        this.videoLoaderElement = this.querySelector(".video-background-loader");
        this.videoPlayerWrapper = this.querySelector(".video-background-player");
        this.sectionWrap = this.closest(".video-background-section-wrap-inner");
        this.playBtn = this.sectionWrap
          ? this.sectionWrap.querySelector(".video-background-play-btn")
          : null;

        if (this.clickToPlay && this.playBtn) {
          this.playBtn.addEventListener("click", () => this.play());
        }

        this.observer = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.intersecting) {
                this.intersecting = true;
                if (this.clickToPlay) {
                  this.prepareVideo();
                } else {
                  this.initializeVideo();
                }
                observer.unobserve(this);
              }
            });
          },
          {
            rootMargin: "200px",
          },
        );

        this.observer.observe(this);
      }

      prepareVideo() {
        switch (this.videoType) {
          case "self-hosted":
            this.prepareSelfHosted();
            break;
          case "youtube":
            VideoBackground.loadYouTube();
            this.prepareYouTube();
            break;
          case "vimeo":
            VideoBackground.loadVimeo();
            this.prepareVimeo();
            break;
        }
      }

      prepareSelfHosted() {
        const player = this.querySelector("video");
        if (!player) return;

        player.removeAttribute("loop");
        player.muted = false;

        if (!this.videoLoaded) {
          this.loadVideoSources(player);
        }

        player.addEventListener("ended", () => this.onVideoEnded());
        player.addEventListener("pause", () => {
          if (!player.ended) this.onVideoPaused();
        });
      }

      prepareYouTube() {
        if (typeof YT === "undefined" || typeof YT.Player === "undefined") {
          return setTimeout(() => this.prepareYouTube(), 333);
        }

        this.ytPlayer = new YT.Player(`${this.sectionId}-video-player`, {
          videoId: this.videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            showinfo: 0,
            modestbranding: 1,
            loop: 0,
            fs: 0,
            mute: 0,
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: () => {
              this.videoLoaderElement.classList.add("is-hidden");
              this.setAttribute("data-loaded", "true");
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.showPlaying();
              }
              if (event.data === YT.PlayerState.ENDED) {
                this.onVideoEnded();
              }
            },
          },
        });
      }

      prepareVimeo() {
        if (typeof Vimeo === "undefined" || typeof Vimeo.Player === "undefined") {
          return setTimeout(() => this.prepareVimeo(), 333);
        }

        this.vimeoPlayer = new Vimeo.Player(this.videoElement, {
          id: this.videoId,
          loop: false,
          autoplay: false,
          byline: false,
          title: false,
          muted: false,
          playsinline: true,
          controls: true,
        });

        this.vimeoPlayer.ready().then(() => {
          this.videoLoaderElement.classList.add("is-hidden");
          this.setAttribute("data-loaded", "true");
        });

        this.vimeoPlayer.on("play", () => this.showPlaying());
        this.vimeoPlayer.on("ended", () => this.onVideoEnded());
      }

      play() {
        if (this.isPlaying) return;

        switch (this.videoType) {
          case "self-hosted": {
            const video = this.querySelector("video");
            if (video) {
              video.controls = true;
              video.play().catch((error) => {
                console.warn("Play failed:", error);
              });
            }
            break;
          }
          case "youtube":
            if (this.ytPlayer && this.ytPlayer.playVideo) {
              this.ytPlayer.playVideo();
            }
            break;
          case "vimeo":
            if (this.vimeoPlayer) {
              this.vimeoPlayer.play();
            }
            break;
        }

        this.showPlaying();
      }

      showPlaying() {
        this.isPlaying = true;
        this.videoPlayerWrapper.classList.add("visible");
        this.videoLoaderElement.classList.add("is-hidden");
        this.setAttribute("data-loaded", "true");

        if (this.sectionWrap) {
          this.sectionWrap.classList.add("video-is-playing");
        }
      }

      onVideoPaused() {
        this.isPlaying = false;
        const video = this.querySelector("video");
        if (video) {
          video.controls = false;
        }
        this.videoPlayerWrapper.classList.remove("visible");

        if (this.sectionWrap) {
          this.sectionWrap.classList.remove("video-is-playing");
        }
      }

      onVideoEnded() {
        this.isPlaying = false;
        this.videoPlayerWrapper.classList.remove("visible");

        if (this.sectionWrap) {
          this.sectionWrap.classList.remove("video-is-playing");
        }

        switch (this.videoType) {
          case "self-hosted": {
            const video = this.querySelector("video");
            if (video) {
              video.controls = false;
              video.pause();
              video.currentTime = 0;
            }
            break;
          }
          case "youtube":
            if (this.ytPlayer && this.ytPlayer.seekTo) {
              this.ytPlayer.pauseVideo();
              this.ytPlayer.seekTo(0);
            }
            break;
          case "vimeo":
            if (this.vimeoPlayer) {
              this.vimeoPlayer.pause();
              this.vimeoPlayer.setCurrentTime(0);
            }
            break;
        }
      }

      initializeVideo() {
        switch (this.videoType) {
          case "self-hosted":
            this.initializeSelfHostedVideo();
            break;
          case "youtube":
            VideoBackground.loadYouTube();
            this.initializeYouTube();
            break;
          case "vimeo":
            VideoBackground.loadVimeo();
            this.initializeVimeo();
            break;
        }
      }

      initializeSelfHostedVideo() {
        const player = this.querySelector("video");

        if (!player) {
          return;
        }

        if (!this.videoLoaded) {
          this.loadVideoSources(player);
        }

        const isVideoPlaying = !!(
          player.currentTime > 0 &&
          !player.paused &&
          !player.ended &&
          player.readyState > 2
        );

        if (isVideoPlaying) {
          this.showPlayer();
        }

        player.addEventListener("loadstart", () => {
          this.videoLoaderElement.classList.remove("is-hidden");
        });

        player.addEventListener("canplay", () => {
          if (!isVideoPlaying) {
            player.play().catch((error) => {
              console.warn("Video autoplay failed:", error);
            });
          }
        });

        player.addEventListener("playing", () => {
          this.showPlayer();
        });

        player.addEventListener("error", () => {
          console.error("Video failed to load");
          this.videoLoaderElement.classList.add("is-hidden");
          this.setAttribute("data-loaded", "error");
        });
      }

      loadVideoSources(player) {
        const sources = player.querySelectorAll("source[data-src]");

        sources.forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute("data-src");
        });

        player.load();
        this.videoLoaded = true;
      }

      initializeYouTube() {
        if (typeof YT === "undefined" || typeof YT.Player === "undefined") {
          return setTimeout(() => {
            this.initializeYouTube();
          }, 333);
        }

        new YT.Player(`${this.sectionId}-video-player`, {
          videoId: this.videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            showinfo: 0,
            modestbranding: 0,
            loop: 1,
            playlist: this.videoId,
            fs: 0,
            autohide: 0,
            mute: 1,
            playsinline: 1,
          },
          events: {
            onReady: (event) => {
              event.target.mute();
              event.target.playVideo();

              setTimeout(() => {
                this.showPlayer();
              }, 1000);
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.showPlayer();
              }
            },
          },
        });
      }

      initializeVimeo() {
        if (typeof Vimeo === "undefined" || typeof Vimeo.Player === "undefined") {
          return setTimeout(() => {
            this.initializeVimeo();
          }, 333);
        }

        const player = new Vimeo.Player(this.videoElement, {
          id: this.videoId,
          loop: true,
          autoplay: true,
          byline: false,
          title: false,
          autopause: false,
          muted: true,
          playsinline: true,
          controls: false,
          background: true,
        });

        player.setVolume(0);
        player.on("play", () => {
          this.showPlayer();
        });
      }

      showPlayer() {
        this.videoPlayerWrapper.classList.add("visible");
        this.videoLoaderElement.classList.add("is-hidden");
        this.setAttribute("data-loaded", "true");
      }

      static loadYouTube() {
        if (window.TFP_YOUTUBE_LOADED) {
          return;
        }

        window.TFP_YOUTUBE_LOADED = true;
        const tag = document.createElement("script");
        const [firstScriptTag] = document.getElementsByTagName("script");
        tag.src = "https://www.youtube.com/player_api";
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      static loadVimeo() {
        if (window.TFP_VIMEO_LOADED) {
          return;
        }

        window.TFP_VIMEO_LOADED = true;
        const tag = document.createElement("script");
        const [firstScriptTag] = document.getElementsByTagName("script");
        tag.src = "https://player.vimeo.com/api/player.js";
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    },
  );
}
